import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Typography,
  Tag,
  Skeleton,
  Progress,
  Tooltip,
  App,
  theme,
  Flex,
  Button,
} from "antd";
import {
  CirclePlay,
  Frown,
  CircleCheck,
  Lock,
  Clock,
  FileText,
} from "lucide-react";
import Icon from "../../../components/Icon";
import {
  fetchCoursesWithModules,
  clearCoursesCache,
} from "../../../services/moduleService";
import { useAuth } from "../../../auth/auth.context";
import ModuleContainerCard from "../../../components/ModuleContainerCard";
import { getSafeImageUrl } from "../../../utils/imageHelper";
import ModuleContainer from "../../../models/ModuleContainer";
import { useThemeMode } from "../../../theme/ThemeProvider";
import { alpha } from "../../../utils/color";

const { Text, Title } = Typography;
const { useToken } = theme;
const COURSE_FALLBACK_IMAGE = "/no-image.jpg";

// Global cache để tránh gọi API nhiều lần khi component mount lại do StrictMode
let globalModulesCache = null;
let globalModulesLoaded = false;

const CARD_HEIGHT = 400;
const IMAGE_HEIGHT = 180;
const CONTENT_HEIGHT = CARD_HEIGHT - IMAGE_HEIGHT; // 220px for content

const getStatusColor = (status, token) => {
  switch (status) {
    case "completed":
      return token.colorSuccess;
    case "in-progress":
      return token.colorPrimary;
    case "locked":
      return token.colorTextSecondary;
    default:
      return token.colorBorder;
  }
};

const clampProgress = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.min(100, Math.max(0, numericValue));
};

const ModernModuleGrid = ({ userProgressData, moduleFromHomePage }) => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState({});
  const { currentUser } = useAuth();
  const { mode } = useThemeMode();
  const { token } = useToken();
  const { message } = App.useApp();

  // Sử dụng userProgressData từ props thay vì gọi useUserProgress hook
  // để tránh duplicate API calls
  const {
    loading: progressLoading,
    isModuleCompleted,
    isModuleInProgress,
    getCurrentStepId,
    userProgress,
  } = userProgressData || {};

  const navigate = useNavigate();

  // Sử dụng ref để theo dõi xem đã tải modules chưa
  const hasLoadedModulesRef = useRef(false);
  // Loại bỏ force refresh để tránh gọi API nhiều lần
  // useUserProgress hook đã tự động load data khi cần thiết

  const loadModules = useCallback(
    async (forceReload = false) => {
      try {
        setLoading(true);

        // Clear cache if force reload
        if (forceReload) {
          clearCoursesCache();
          globalModulesCache = null;
          globalModulesLoaded = false;
        }

        // Kiểm tra global cache trước
        if (!forceReload && globalModulesCache && globalModulesLoaded) {
          setModules(globalModulesCache);
          setLoading(false);
          return;
        }

        // const data = await fetchCoursesWithModules(currentUser?.uid);
        if (!moduleFromHomePage || moduleFromHomePage.length === 0) {
          setModules([]);
          setLoading(false);
          return;
        }

        const data = moduleFromHomePage;

        // Lưu vào global cache
        globalModulesCache = data;
        globalModulesLoaded = true;

        setModules(data);
        setLoading(false);
        hasLoadedModulesRef.current = true;
      } catch (error) {
        console.error("Error fetching courses with modules:", error);
        setLoading(false);
      }
    },
    [currentUser?.uid, moduleFromHomePage]
  );

  useEffect(() => {
    // Chỉ load nếu chưa có data
    loadModules();
  }, [loadModules]); // Reload when user changes

  // Expose reload function globally for admin panel to trigger
  useEffect(() => {
    window.reloadModulesData = () => loadModules(true);
    return () => {
      delete window.reloadModulesData;
    };
  }, [loadModules]);

  const handleImageLoad = (id) => {
    setImageLoaded((prev) => ({ ...prev, [id]: true }));
  };

  const handleModuleClick = (module, index) => {
    // Check if this is a ModuleContainer
    if (module.type === "container") {
      // ModuleContainerCard will handle its own click logic
      return;
    }

    // Get current module status
    const status = getModuleStatus(module, index);
    // Prevent navigation if module is locked
    if (status === "locked") {
      // Show different messages based on user login status
      if (!currentUser) {
        message.warning(`Please log in to access "${module.name}".`);
      } else {
        message.warning(
          `Module "${module.name}" is locked. Complete previous modules first.`
        );
      }
      return;
    }

    // Navigate to module if not locked
    navigate(`/module/${module.id}`);
  };

  // Sử dụng ref để cache trạng thái của module
  const moduleStatusCacheRef = useRef({});

  // Clear cache khi user progress data thay đổi (không phải functions)
  // Sử dụng JSON.stringify để so sánh deep equality
  const userProgressStringRef = useRef("");
  useEffect(() => {
    const currentProgressString = JSON.stringify(userProgress);
    if (currentProgressString !== userProgressStringRef.current) {
      moduleStatusCacheRef.current = {};
      moduleProgressCacheRef.current = {};
      userProgressStringRef.current = currentProgressString;
    }
  }, [userProgress]);

  const getModuleStatus = useCallback(
    (module, index) => {
      // Sử dụng cache để tránh tính toán lại không cần thiết
      const cacheKey = `${module.id
        }_${index}_${!!currentUser}_${progressLoading}`;
      if (moduleStatusCacheRef.current[cacheKey]) {
        return moduleStatusCacheRef.current[cacheKey];
      }

      let status;

      // If user is not logged in, all modules except first are locked
      if (!currentUser) {
        if (index === 0) {
          status = "not-started";
        } else {
          status = "locked";
        }
        moduleStatusCacheRef.current[cacheKey] = status;
        return status;
      }

      // If user progress is still loading, return appropriate status
      if (progressLoading) {
        status = index === 0 ? "not-started" : "locked";
        moduleStatusCacheRef.current[cacheKey] = status;
        return status;
      }

      // First module is always unlocked for logged in users
      if (index === 0) {
        if (isModuleCompleted(module.id)) {
          status = "completed";
        } else if (isModuleInProgress(module.id)) {
          status = "in-progress";
        } else {
          status = "not-started";
        }
      } else {
        // For other modules, check if previous module is completed
        const previousModule = modules[index - 1];

        if (!previousModule) {
          status = "locked";
        } else {
          const isPreviousCompleted = isModuleCompleted(previousModule.id);

          if (isModuleCompleted(module.id)) {
            status = "completed";
          } else if (isModuleInProgress(module.id)) {
            status = "in-progress";
          } else if (isPreviousCompleted) {
            status = "not-started";
          } else {
            status = "locked";
          }
        }
      }

      // Cache kết quả
      moduleStatusCacheRef.current[cacheKey] = status;
      return status;
    },
    [
      isModuleCompleted,
      isModuleInProgress,
      modules,
      currentUser,
      progressLoading,
    ]
  );

  const getModuleStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <Icon as={CircleCheck} strokeWidth={2.5} />;
      case "in-progress":
        return <Icon as={CirclePlay} />;
      case "updated":
        return <Icon as={CirclePlay} />;
      case "locked":
        return <Icon as={Lock} />;
      default:
        return null;
    }
  };

  // Sử dụng ref để cache tiến trình của module
  const moduleProgressCacheRef = useRef({});

  const getModuleProgress = useCallback(
    (module) => {
      // Kiểm tra cache trước
      if (moduleProgressCacheRef.current[module.id] !== undefined) {
        return moduleProgressCacheRef.current[module.id];
      }

      const currentStepId = getCurrentStepId(module.id);
      if (!currentStepId || !module.totalSteps) {
        moduleProgressCacheRef.current[module.id] = 0;
        return 0;
      }

      // Extract step number from step ID (assuming format like "step1", "step2", etc.)
      const stepNumber = parseInt(currentStepId.replace(/\D/g, ""), 10);
      const progress = clampProgress((stepNumber / module.totalSteps) * 100);

      // Lưu vào cache
      moduleProgressCacheRef.current[module.id] = progress;

      return progress;
    },
    [getCurrentStepId]
  );

  // Show loading if modules are loading OR if user progress is loading for logged in users
  const isLoading = loading || (currentUser && progressLoading);

  if (!isLoading && modules.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "64px 0", color: token.colorTextSecondary }}>
        <Typography.Title level={4} style={{ color: "inherit" }}>No courses found</Typography.Title>
        <Text type="secondary">Please check back later.</Text>
      </div>
    );
  }

  const skeletonBg =
    mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)";

  return (
    <>
      <style>{`
        .course-card {
          height: 100%;
          display: flex;
          flex-direction: column;
          border-radius: ${token.borderRadius}px;
          overflow: hidden;
          border: 1px solid ${token.colorBorderSecondary};
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          background: ${mode === "dark" ? token.colorBgContainer : "#fff"};
          font-family: var(--font-untitled-sans);
        }
        .course-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 30px ${alpha(token.colorText, 0.1)};
        }
        .course-card-cover {
          position: relative;
          height: 220px;
          overflow: hidden;
        }
        .course-card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        .course-card:hover .course-card-image {
          transform: scale(1.08);
        }
        .course-card-tag {
          position: absolute;
          top: 16px;
          left: 16px;
          backdrop-filter: blur(4px);
          padding: 4px 12px;
          border-radius: ${token.borderRadius / 2}px;
          font-weight: 600;
          font-size: 12px;
          z-index: 2;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          font-family: var(--font-untitled-sans);
        }
        .course-card-content {
          padding: 24px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .course-card-footer {
          padding: 16px 24px;
          border-top: 1px solid ${token.colorBorderSecondary};
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: ${mode === "dark" ? "rgba(255, 255, 255, 0.01)" : "rgba(0, 0, 0, 0.01)"};
        }
        .module-play-button {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: ${token.colorPrimary};
          color: ${token.colorTextLightSolid};
          border-radius: 50%;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px ${alpha(token.colorPrimary, 0.2)};
          opacity: 0;
          z-index: 2;
          font-size: 24px;
          transition: all 0.3s ease;
        }
        .course-card:hover .module-play-button {
          opacity: 0.9;
          transform: translate(-50%, -50%) scale(1.1);
        }
      `}</style>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 32,
          width: "100%",
        }}
      >
        <style>{`
          @media (min-width: 576px) {
            .module-grid { grid-template-columns: repeat(2, 1fr) !important; }
          }
          @media (min-width: 768px) {
            .module-grid { grid-template-columns: repeat(3, 1fr) !important; }
          }
        `}</style>
        <div
          className="module-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 32,
            width: "100%",
          }}
        >
          {(isLoading
            ? Array.from(new Array(6))
            : modules.filter((item) => {
              // Filter out locked modules (but keep containers/courses)
              if (item.isContainer) {
                return true; // Always show courses/containers
              }
              return !item.isLocked; // Hide locked individual modules
            })
          ).map((item, idx) => {
            if (isLoading) {
              return (
                <Card
                  key={idx}
                  bodyStyle={{ padding: 0, display: "flex", flexDirection: "column", height: "100%" }}
                  style={{
                    height: CARD_HEIGHT,
                    width: "100%",
                    borderRadius: 12,
                    overflow: "hidden",
                  }}
                >
                  <Skeleton.Image
                    active
                    style={{
                      width: "100%",
                      height: IMAGE_HEIGHT,
                      borderRadius: "12px 12px 0 0",
                      background: skeletonBg,
                    }}
                  />
                  <div style={{ padding: 16, flex: 1 }}>
                    <Skeleton
                      active
                      title={{ width: "80%", style: { height: 40, background: skeletonBg } }}
                      paragraph={{
                        rows: 2,
                        width: ["100%", "60%"],
                      }}
                    />
                    <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                      <Skeleton.Button
                        active
                        size="small"
                        style={{
                          width: 60,
                          height: 24,
                          borderRadius: 20,
                          background: skeletonBg,
                        }}
                      />
                      <Skeleton.Button
                        active
                        size="small"
                        style={{
                          width: 60,
                          height: 24,
                          borderRadius: 20,
                          background: skeletonBg,
                        }}
                      />
                    </div>
                  </div>
                </Card>
              );
            }

            // Check if this is a ModuleContainer
            if (item.isContainer) {
              const isCompletedCourse = item.isCompleted(userProgress);
              let isLocked = false;

              if (!isCompletedCourse && idx > 0) {
                const previousCourse = [...modules]
                  .slice(0, idx)
                  .reverse()
                  .find((course) => course.isContainer && !course.isComingSoon);
                if (previousCourse) {
                  isLocked =
                    !previousCourse.isCompleted(userProgress);
                }
              }
              let isUpdate = false;
              if (userProgress && !modules[idx].isComingSoon && !isLocked)
                for (const module of modules[idx].modules) {
                  const progressModule = userProgress[module.id];
                  if (!progressModule) continue;
                  if (progressModule.version < module.version) isUpdate = true;
                }

              // Tạo lại instance ModuleContainer để giữ prototype
              const containerInstance = new ModuleContainer({
                ...item,
                isLocked,
                isUpdate,
              });
              return (
                <ModuleContainerCard
                  key={item.id}
                  container={containerInstance}
                />
              );
            }

            // Original module card rendering for individual modules
            const module = item;
            const imgSrc = getSafeImageUrl(module.thumbnail) || COURSE_FALLBACK_IMAGE;
            const status = getModuleStatus(module, idx);
            const isLockedModule = status === "locked";
            const moduleProgressPercent = currentUser ? Math.round(getModuleProgress(module)) : 0;
            
            // Check for update on individual module
            const progress = userProgress ? userProgress[module.id] : null;
            const hasUpdate = progress && progress.version < module.version;

            return (
              <div 
                key={module.id} 
                className="course-card" 
                onClick={() => handleModuleClick(module, idx)}
                style={{ cursor: isLockedModule ? "not-allowed" : "pointer", opacity: isLockedModule ? 0.7 : 1 }}
              >
                <div className="course-card-cover">
                  <div 
                    className="course-card-tag"
                    style={{ 
                      background: hasUpdate ? "#111827" : "rgba(255, 255, 255, 0.9)",
                      color: hasUpdate ? "#fff" : "#111827"
                    }}
                  >
                    {hasUpdate ? "updated" : "Module"}
                  </div>
                  <img 
                    src={imgSrc} 
                    alt={module.name} 
                    className="course-card-image"
                    style={{ filter: isLockedModule ? "grayscale(1)" : "none" }}
                    onError={(event) => {
                      if (!event.currentTarget.src.endsWith(COURSE_FALLBACK_IMAGE)) {
                        event.currentTarget.src = COURSE_FALLBACK_IMAGE;
                      }
                    }}
                  />
                  {isLockedModule && (
                    <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3 }}>
                      <Icon as={Lock} size={40} style={{ color: "#fff" }} />
                    </div>
                  )}
                  {!isLockedModule && (
                    <div className="module-play-button">
                      <Icon as={CirclePlay} />
                    </div>
                  )}
                </div>

                <div className="course-card-content">
                  <Title level={4} style={{ 
                    margin: "0 0 12px 0", 
                    fontSize: "1.25rem",
                  }}>
                    {module.name}
                  </Title>
                  <Text type="secondary" style={{ 
                    display: "-webkit-box", 
                    WebkitLineClamp: 3, 
                    WebkitBoxOrient: "vertical", 
                    overflow: "hidden",
                    lineHeight: "1.6",
                    fontSize: "14px",
                    minHeight: "68px",
                    fontWeight: 400
                  }}>
                    {module.description}
                  </Text>

                  {/* Tags list back again */}
                  <div style={{ marginTop: 12 }}>
                    <Flex wrap="wrap" gap={6}>
                      {module.tags?.slice(0, 3).map((tag, index) => (
                        <Tag
                          key={index}
                          bordered={false}
                          style={{
                            backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.05)",
                            fontSize: "11px",
                            fontWeight: 500,
                            margin: 0,
                            borderRadius: "4px",
                            color: token.colorTextSecondary
                          }}
                        >
                          {tag}
                        </Tag>
                      ))}
                    </Flex>
                  </div>

                  {/* Statistics */}
                  <div style={{ marginTop: "auto", paddingTop: 16 }}>
                    <Flex gap={12} wrap="wrap">
                       <Flex align="center" gap={4}>
                          <Icon as={Clock} size={14} style={{ color: token.colorText }} />
                          <Text type="secondary" style={{ fontSize: 12, fontWeight: 500 }}>
                            {module.totalSteps} Topics
                          </Text>
                       </Flex>
                       {module.totalQuizQuestions > 0 && (
                         <Flex align="center" gap={4}>
                            <Icon as={FileText} size={14} style={{ color: token.colorText }} />
                            <Text type="secondary" style={{ fontSize: 12, fontWeight: 500 }}>
                              {module.totalQuizQuestions} Quizzes
                            </Text>
                         </Flex>
                       )}
                    </Flex>
                  </div>
                </div>

                <div className="course-card-footer">
                  <div style={{ flex: 1, marginRight: 16 }}>
                    {currentUser && isModuleInProgress(module.id) && (
                      <>
                        <Progress 
                          percent={moduleProgressPercent}
                          size="small" 
                          showInfo={false} 
                          strokeColor={token.colorText}
                          trailColor={alpha(token.colorText, 0.06)}
                          strokeWidth={8}
                        />
                        <Text type="secondary" style={{ fontSize: 11, fontWeight: 700 }}>
                          {moduleProgressPercent}% DONE
                        </Text>
                      </>
                    )}
                    {isModuleCompleted(module.id) && (
                      <Text type="secondary" style={{ fontSize: 11, fontWeight: 700, color: token.colorText }}>
                        COMPLETED
                      </Text>
                    )}
                  </div>
                  <Button 
                    type="primary" 
                    disabled={isLockedModule}
                    style={{ 
                      borderRadius: token.borderRadius, 
                      fontWeight: 400,
                      background: isModuleCompleted(module.id) ? token.colorText : isLockedModule ? "rgba(0,0,0,0.4)" : token.colorPrimary,
                      color: token.colorTextLightSolid,
                      border: "none",
                      padding: "0 20px"
                    }}
                  >
                    {isModuleCompleted(module.id) ? "Review" : isModuleInProgress(module.id) ? "Resume" : "Start Learning"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default ModernModuleGrid;
